import React from "react";
import { useParams } from "react-router-dom";
import { useGetTransactionDetailsQuery } from "../../../features/ebills/ebillsSlice";
import { Loader } from "../../../shared/Loader";
import { formatDateWithTimeAndDate } from "../../../utils/formatDate";

export const BillsTransactionDetails = () => {
  const { requestId } = useParams<{ requestId: string }>();

  const {
    data: transaction,
    isLoading,
    isFetching,
    isError,
  } = useGetTransactionDetailsQuery(requestId || "");

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "completed") {
      return (
        <div className="badge-success px-4 py-2 rounded-full text-xs inline-block">
          Completed
        </div>
      );
    } else if (statusLower === "failed") {
      return (
        <div className="badge-error px-4 py-2 rounded-full text-xs inline-block">
          Failed
        </div>
      );
    } else if (statusLower === "pending") {
      return (
        <div className="badge-warning px-4 py-2 rounded-full text-xs inline-block">
          Pending
        </div>
      );
    } else {
      return (
        <div className="badge-info px-4 py-2 rounded-full text-xs inline-block">
          {status}
        </div>
      );
    }
  };

  return (
    <div className="font-dashboard pt-4 pb-12 md:px-4 min-h-screen">
      <div className="max-w-[700px] mx-auto relative">
        <div className="bg-black flex flex-col gap-4 px-4 lg:px-10 py-10 min-h-[85vh]">
          <h3 className="text-2xl text-white">Transaction Details</h3>
          <hr className="border-white/10" />

          {isLoading || isFetching ? (
            <div className="w-full h-[70vh] flex justify-center items-center">
              <Loader />
            </div>
          ) : isError || !transaction ? (
            <div className="w-full h-[70vh] flex justify-center items-center">
              <p className="text-white/60">Error loading transaction details</p>
            </div>
          ) : (
            <>
              <table className="flex flex-col gap-2">
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Request ID</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    <div className="flex items-center gap-2">
                      <span>{transaction.request_id}</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(transaction.request_id);
                        }}
                        className="text-white/60 hover:text-white"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Order ID</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    {transaction.order_id}
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Product</p>
                  </td>
                  <td className="text-sm font-light text-white/40 capitalize">
                    {transaction.product}
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Service</p>
                  </td>
                  <td className="text-sm font-light text-white/40 capitalize">
                    {transaction.service_id || "N/A"}
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Status</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    {getStatusBadge(transaction.status)}
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Base Amount</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    ₦{transaction.base_amount.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Fee</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    ₦{transaction.fee.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Total</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    ₦{transaction.total.toLocaleString()}
                  </td>
                </tr>
                {transaction.phone && (
                  <tr>
                    <td className="w-[200px]">
                      <p className="text-base text-white/70">Phone Number</p>
                    </td>
                    <td className="text-sm font-light text-white/40">
                      {transaction.phone}
                    </td>
                  </tr>
                )}
                {transaction.customer_id && (
                  <tr>
                    <td className="w-[200px]">
                      <p className="text-base text-white/70">
                        {transaction.product === "electricity"
                          ? "Meter Number"
                          : transaction.product === "tv"
                          ? "Smartcard/IUC"
                          : transaction.product === "betting"
                          ? "Customer ID"
                          : "Customer ID"}
                      </p>
                    </td>
                    <td className="text-sm font-light text-white/40">
                      {transaction.customer_id}
                    </td>
                  </tr>
                )}
                {transaction.variation_id && (
                  <tr>
                    <td className="w-[200px]">
                      <p className="text-base text-white/70">
                        {transaction.product === "data"
                          ? "Data Plan"
                          : transaction.product === "tv"
                          ? "Package"
                          : transaction.product === "electricity"
                          ? "Meter Type"
                          : "Variation ID"}
                      </p>
                    </td>
                    <td className="text-sm font-light text-white/40">
                      {transaction.variation_id}
                    </td>
                  </tr>
                )}
                {transaction.subscription_type && (
                  <tr>
                    <td className="w-[200px]">
                      <p className="text-base text-white/70">
                        Subscription Type
                      </p>
                    </td>
                    <td className="text-sm font-light text-white/40 capitalize">
                      {transaction.subscription_type}
                    </td>
                  </tr>
                )}
                {transaction.value !== null && (
                  <tr>
                    <td className="w-[200px]">
                      <p className="text-base text-white/70">
                        {transaction.product === "epins"
                          ? "PIN Value"
                          : "Value"}
                      </p>
                    </td>
                    <td className="text-sm font-light text-white/40">
                      ₦{transaction.value.toLocaleString()}
                    </td>
                  </tr>
                )}
                {transaction.quantity !== null && (
                  <tr>
                    <td className="w-[200px]">
                      <p className="text-base text-white/70">
                        {transaction.product === "epins"
                          ? "Quantity"
                          : "Quantity"}
                      </p>
                    </td>
                    <td className="text-sm font-light text-white/40">
                      {transaction.quantity}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Created At</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    {formatDateWithTimeAndDate(transaction.created_at)}
                  </td>
                </tr>
                <tr>
                  <td className="w-[200px]">
                    <p className="text-base text-white/70">Updated At</p>
                  </td>
                  <td className="text-sm font-light text-white/40">
                    {formatDateWithTimeAndDate(transaction.updated_at)}
                  </td>
                </tr>
              </table>

              {transaction.api_data && (
                <div className="mt-6">
                  <h4 className="text-lg text-white/70 mb-4">API Data</h4>
                  <table className="flex flex-col gap-2">
                    <tr>
                      <td className="w-[200px]">
                        <p className="text-base text-white/70">API Order ID</p>
                      </td>
                      <td className="text-sm font-light text-white/40">
                        {transaction.api_data.order_id}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[200px]">
                        <p className="text-base text-white/70">API Status</p>
                      </td>
                      <td className="text-sm font-light text-white/40">
                        {transaction.api_data.status}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[200px]">
                        <p className="text-base text-white/70">Product Name</p>
                      </td>
                      <td className="text-sm font-light text-white/40">
                        {transaction.api_data.product_name}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[200px]">
                        <p className="text-base text-white/70">Service Name</p>
                      </td>
                      <td className="text-sm font-light text-white/40">
                        {transaction.api_data.service_name}
                      </td>
                    </tr>
                  </table>
                </div>
              )}

              {transaction.epins && transaction.epins.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg text-white/70 mb-4">ePins</h4>
                  <div className="flex flex-col gap-4">
                    {transaction.epins.map((pin, index) => (
                      <div
                        key={index}
                        className="bg-gray-700 rounded-lg p-4 border border-white/10"
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex justify-between">
                            <span className="text-white/60">Amount:</span>
                            <span className="text-white">
                              ₦{pin.amount}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">PIN:</span>
                            <span className="text-white font-mono">
                              {pin.pin}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">Serial:</span>
                            <span className="text-white font-mono">
                              {pin.serial}
                            </span>
                          </div>
                          {pin.instruction && (
                            <div className="mt-2 pt-2 border-t border-white/10">
                              <span className="text-white/60">
                                {pin.instruction}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

